import { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";

export const isMobile = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || (navigator.maxTouchPoints > 1 && /Mac/.test(navigator.userAgent));

async function toFile(src, filename) {
  const blob = src instanceof Blob ? src : await (await fetch(src)).blob();
  return new File([blob], filename, { type: blob.type || "image/png" });
}

function plainDownload(src, filename) {
  const a = document.createElement("a");
  a.href = src instanceof Blob ? URL.createObjectURL(src) : src;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  if (src instanceof Blob) setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

/* Returns "shared" | "downloaded" | "fallback". On mobile opens the native share sheet so the user can tap Save Image. */
export async function saveImage(src, filename, title) {
  if (!isMobile()) { plainDownload(src, filename); return "downloaded"; }
  try {
    const file = await toFile(src, filename);
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title });
      return "shared";
    }
  } catch (e) {
    if (e?.name === "AbortError") return "shared";
  }
  return "fallback";
}

export function SaveImageSheet({ src, onClose }) {
  if (!src) return null;
  return (
    <div className="fixed inset-0 z-[110] flex flex-col items-center justify-center bg-sbg-burgundy/95 p-5" data-testid="save-image-sheet" onClick={onClose}>
      <button onClick={onClose} aria-label="Close" className="absolute right-4 top-4 rounded-full bg-white/15 p-2 text-white"><X className="h-5 w-5" /></button>
      <p className="mb-4 text-center font-display text-xl font-black text-white">Press and hold the image, then tap <span className="text-sbg-turquoise">Save to Photos</span></p>
      <img src={src} alt="Save to your photos" className="max-h-[70vh] w-auto max-w-full rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()} />
      <p className="mt-4 text-center text-xs text-sbg-blush/80">Tap anywhere outside the image to close</p>
    </div>
  );
}

/* Hook: wire up a button + fallback sheet in one go */
export function useSaveImage() {
  const [sheetSrc, setSheetSrc] = useState(null);
  const save = async (src, filename, title) => {
    const result = await saveImage(src, filename, title);
    if (result === "fallback") {
      setSheetSrc(src instanceof Blob ? URL.createObjectURL(src) : src);
    } else if (result === "downloaded") {
      toast.success("Saved! Check your Downloads folder.");
    }
    return result;
  };
  const sheet = <SaveImageSheet src={sheetSrc} onClose={() => setSheetSrc(null)} />;
  return { save, sheet };
}
